import React, { ProviderProps, ReactNode, useEffect, useMemo, useState } from "react";
import { ALL_CRS_INFO as ALL_CRS_INFOS, CRS_INFO_STD } from "../components/MagicCursor";
import { ALL_SITE_INFOS, SITE_INFO_TROK } from "./SitesProvider";

export type PlyData = {
    sites: string[];
    cursors: string[];
}

const EmptyData = () : PlyData => {
    return {
        cursors: [ CRS_INFO_STD.key ],
        sites: [ SITE_INFO_TROK.key ]
    }
}

const LCLKEY : string = 'trok19ply97';

const LoadedData = () : PlyData => {
    const cachedStateString : string|null = localStorage.getItem(LCLKEY);
    const cachedState : PlyData = cachedStateString !== null ? JSON.parse(cachedStateString) : EmptyData();
    const cachedCursors : string[] = [...cachedState.cursors];
    const cachedSites : string[] = [...cachedState.sites];
    return {
        cursors: cachedCursors,
        sites: cachedSites
    };
}

export type PlayerContext = {
    data: PlyData;
    unlockSite: (site: string) => void;
    hasUnlockedSite: (site: string) => boolean;
    unlockCursor: (cursor: string) => void;
    hasUnlockedCursor: (cursor: string) => boolean;
    debugClearAll : () => void;
    debugUnlockAll : () => void;
};

const context = React.createContext<PlayerContext>({
    data: LoadedData(),
    unlockSite: (s: string) => {},
    hasUnlockedSite: (s: string) => { return false; },
    unlockCursor: (c: string) => {},
    hasUnlockedCursor: (c: string) => { return false; },
    debugClearAll: () => {},
    debugUnlockAll: () => {}
});

const PlayerProvider = (props: { children : ReactNode }) => {

    const [data, setData] = useState<PlyData>(LoadedData());
    
    const unlockCursor = (c: string) => {
        if (hasUnlockedCursor(c))
            return;
        const newData : PlyData = {
            cursors: [...data.cursors],
            sites: [...data.sites]
        }
        newData.cursors.push(c);
        localStorage.setItem(LCLKEY, JSON.stringify(newData));
        setData(newData);
    };

    const hasUnlockedCursor = (c: string) => {
        return data.cursors.includes(c);
    }

    const unlockSite = (s: string) => {
        if (hasUnlockedSite(s))
            return;
        const newData : PlyData = {
            cursors: [...data.cursors],
            sites: [...data.sites]
        }
        newData.sites.push(s);
        localStorage.setItem(LCLKEY, JSON.stringify(newData));
        setData(newData);
    }

    const hasUnlockedSite = (s: string) => {
        return data.sites.includes(s);
    }

    const debugClearAll = () => {
        localStorage.removeItem(LCLKEY);
        setData(EmptyData());
    };

    const debugUnlockAll = () => {
        const newData : PlyData = {cursors:[], sites:[]};
        ALL_SITE_INFOS.forEach(s => { newData.sites.push(s.key); });
        ALL_CRS_INFOS.forEach(c => { newData.cursors.push(c.key); });
        localStorage.setItem(LCLKEY, JSON.stringify(newData));
        setData(newData);
    };

    const value = useMemo(() : PlayerContext => {
        return { 
            data, 
            unlockSite, 
            hasUnlockedSite, 
            unlockCursor, 
            hasUnlockedCursor, 
            debugClearAll, 
            debugUnlockAll 
        };
    }, [data]);

    const children = useMemo(() => {
        return props.children;
    }, [props]);

    return (
        <context.Provider value={value}>
            { children }
        </context.Provider>
    );
};

PlayerProvider.context = context;

export default PlayerProvider;